import React, { ReactNode, useContext, useMemo } from 'react';

import {
  EditContext,
  RecordContextProvider,
  ReferenceField,
  ReferenceFieldBase,
  usePermissions,
  useRecordContext,
} from 'react-admin';

import { get } from 'lodash';

import { Box, Typography } from '@mui/material';

import entityTypes, { EntityTypes, KnownEntityTypes, resourceFromEntityTypes } from '../constants/entityTypes';
import { useScopedEmbeddedViewContext } from '../components/utils/ScopedEmbeddedViewContext';
import hasAccess from '../utilities/hasAccess';
import { Actions } from '../constants/actions';
import entityListPermissionMap from '../constants/entityPermissionMap';
import PermissionMissing from '../components/utils/PermissionMissing';

export const EntityContext = React.createContext<{
  type: EntityTypes;
  id: string;
  hasLink: boolean;
} | null>(null);

const ReferenceEntityField = ({
  source,
  label,
  children,
  linkType = 'edit',
  emptyText = '-',
  idKey = 'id',
  typeKey = 'type',
  entityType,
  useBaseVersion = false,
}: {
  linkType?: string | false;
  source: string;
  children: ReactNode;
  label?: string;
  sortable?: boolean;
  emptyText?: string;
  idKey?: string;
  typeKey?: string;
  entityType?: EntityTypes;
  useBaseVersion?: boolean;
}) => {
  const { record: parentRecord } = useContext(EditContext) ?? {};
  const record = useRecordContext();
  const value = source ? get(record, source, {}) : record;
  const idSource = source ? `${source}.${idKey}` : idKey;

  const id = get(value, idKey);
  const type = entityType ?? get(value, typeKey);

  const scope = useScopedEmbeddedViewContext();

  const link = scope.isScopedEmbedded || parentRecord?.id === id ? false : linkType;

  const entityContextValue = useMemo(() => ({
    type,
    id,
    hasLink: type === entityTypes.UNKNOWN ? false : !!link,
  }), [type, id, link]);

  const { permissions } = usePermissions<Actions[]>();

  if (!type) return <Typography>{emptyText}</Typography>;

  if (type === entityTypes.UNKNOWN) {
    return (
      <EntityContext.Provider value={entityContextValue}>
        <RecordContextProvider value={value}>
          {children}
        </RecordContextProvider>
      </EntityContext.Provider>
    );
  }

  if (
    !hasAccess(permissions, entityListPermissionMap[type as KnownEntityTypes])
  ) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <PermissionMissing />
      </Box>
    );
  }

  return (
    <EntityContext.Provider value={entityContextValue}>
      {!useBaseVersion ? (
        <ReferenceField
          label={label}
          source={idSource}
          reference={resourceFromEntityTypes[type as KnownEntityTypes]}
          link={link}
        >
          {children}
        </ReferenceField>
      ) : (
        <ReferenceFieldBase
          source={idSource}
          reference={resourceFromEntityTypes[type as KnownEntityTypes]}
        >
          {children}
        </ReferenceFieldBase>
      )}
    </EntityContext.Provider>
  );
};

export default ReferenceEntityField;
